import React, { useEffect, useState } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { getData, postData } from "../../services/request"

const AdminDisputeDetails = () => {
  const { disputeId } = useParams()
  const navigate = useNavigate()
  const [dispute, setDispute] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [resolutionNotes, setResolutionNotes] = useState("")
  const [resolving, setResolving] = useState(false)

  const fetchDispute = async () => {
    setLoading(true)
    setError("")
    setSuccess("")
    const url = `${getAPIBaseURL()}/v1/disputes/${disputeId}`
    const res = await getData(url, {}, {})
    setLoading(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        setDispute(res.result.data?.data)
      } else {
        setError(res.result.data?.message || "Failed to load dispute")
      }
    } else {
      setError(res.result?.data?.message || "Network error")
    }
  }

  useEffect(() => {
    fetchDispute()
  }, [disputeId])

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (error) {
      return iso
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "resolved":
        return "bg-green-100 text-green-700"
      case "open":
        return "bg-yellow-100 text-yellow-700"
      default:
        return "bg-gray-100 text-gray-700"
    }
  }

  const getRoleColor = (role) => {
    switch (role) {
      case "HOSPITAL":
        return "bg-blue-100 text-blue-700"
      case "PATIENT":
        return "bg-purple-100 text-purple-700"
      default:
        return "bg-gray-100 text-gray-700"
    }
  }

  const handleResolve = async () => {
    if (!window.confirm("Mark this dispute as resolved?")) {
      return
    }

    setResolving(true)
    setError("")
    setSuccess("")

    const url = `${getAPIBaseURL()}/v1/disputes/${disputeId}/resolve`
    const data = {}
    if (resolutionNotes.trim()) {
      data.resolutionNotes = resolutionNotes.trim()
    }

    const res = await postData(url, {}, data)
    setResolving(false)

    if (res.error == "") {
      if (res.result.status === 200) {
        setSuccess("Dispute marked as resolved.")
        setResolutionNotes("")
        fetchDispute()
      } else {
        setError(res.result.data?.message || "Failed to resolve dispute")
      }
    } else {
      setError(res.result?.data?.message || "Network error")
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="p-6 text-sm text-gray-500">Loading dispute...</div>
      </div>
    )
  }

  if (error && !dispute) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
        <button
          onClick={() => navigate("/admin/app/disputes")}
          className="mt-4 rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-900"
        >
          Back to Disputes
        </button>
      </div>
    )
  }

  if (!dispute) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="p-6 text-sm text-gray-500">Dispute not found</div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dispute Details</h1>
          <p className="mt-1 text-sm text-gray-500">Review and resolve this dispute</p>
        </div>
        <button
          onClick={() => navigate("/admin/app/disputes")}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back to Disputes
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm font-medium text-green-700">
          {success}
        </div>
      )}

      <div className="space-y-6">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${getStatusColor(dispute.status)}`}>
                {dispute.status?.toUpperCase() || "UNKNOWN"}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              Created: <span className="font-medium text-gray-900">{formatDate(dispute.createdAt)}</span>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Subject & Message</h2>
          <div className="space-y-4 text-sm text-gray-700">
            <div>
              <p className="text-xs font-medium uppercase text-gray-500">Subject</p>
              <p className="mt-1 text-base font-semibold text-gray-900">{dispute.subject}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase text-gray-500">Message</p>
              <p className="mt-1 whitespace-pre-line">{dispute.message}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Created By</h2>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${getRoleColor(dispute.createdByRole)}`}>
                {dispute.createdByRole}
              </span>
              <span className="font-medium text-gray-900">
                {dispute.createdBy?.organization || dispute.createdBy?.name || "Unknown"}
              </span>
            </div>
            {dispute.createdBy?.email && (
              <div>
                <span className="font-medium text-gray-500">Email:</span>
                <span className="ml-2 text-gray-900">{dispute.createdBy.email}</span>
              </div>
            )}
            {dispute.createdBy?.phoneNumber && (
              <div>
                <span className="font-medium text-gray-500">Phone:</span>
                <span className="ml-2 text-gray-900">{dispute.createdBy.phoneNumber}</span>
              </div>
            )}
            {dispute.createdBy?._id && (
              <div className="pt-2">
                <Link
                  to={
                    dispute.createdByRole === "HOSPITAL"
                      ? `/admin/app/hospitals/${dispute.createdBy._id}`
                      : `/admin/app/patients/${dispute.createdBy._id}`
                  }
                  className="text-sm font-medium text-black hover:underline"
                >
                  View {dispute.createdByRole === "HOSPITAL" ? "Hospital" : "Patient"} Details
                </Link>
              </div>
            )}
          </div>
        </div>

        {dispute.postId && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Related Request</h2>
                <p className="mt-1 text-sm text-gray-500">Message: {dispute.postId.message || "—"}</p>
                {dispute.postId.status && (
                  <p className="text-xs text-gray-500">Status: {dispute.postId.status}</p>
                )}
              </div>
              <Link
                to={`/admin/app/requests/${dispute.postId._id || dispute.postId}`}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                View Request
              </Link>
            </div>
          </div>
        )}

        {dispute.status === "resolved" && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-green-900">Resolution</h2>
            <div className="space-y-2 text-sm text-green-900">
              {dispute.resolvedBy && (
                <div>
                  <span className="font-medium">Resolved By:</span>
                  <span className="ml-2">
                    {dispute.resolvedBy?.name || dispute.resolvedBy?.email || "Admin"}
                  </span>
                </div>
              )}
              {dispute.resolvedAt && (
                <div>
                  <span className="font-medium">Resolved At:</span>
                  <span className="ml-2">{formatDate(dispute.resolvedAt)}</span>
                </div>
              )}
              {dispute.resolutionNotes && (
                <div>
                  <span className="font-medium">Notes:</span>
                  <p className="mt-1 whitespace-pre-line">{dispute.resolutionNotes}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {dispute.status === "open" && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Resolve Dispute</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Resolution Notes (optional)
                </label>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  rows={4}
                  placeholder="Share details about how this dispute was resolved..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black resize-none"
                />
              </div>
              <button
                onClick={handleResolve}
                disabled={resolving}
                className="w-full rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {resolving ? "Resolving..." : "Mark as Resolved"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminDisputeDetails


